import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import "./protected_persons_modern.scss";
import AddIcon from "@mui/icons-material/Add";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import TopicOutlinedIcon from "@mui/icons-material/TopicOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";

const ASSET_TYPES = [
  { value: "person", label: "Person", icon: PersonOutlineIcon },
  { value: "place", label: "Location", icon: PlaceOutlinedIcon },
  { value: "event", label: "Event", icon: EventOutlinedIcon },
  { value: "topic", label: "Topic", icon: TopicOutlinedIcon },
];

const splitTerms = (value) =>
  value
    ? value
      .split(",")
      .map((term) => term.trim())
      .filter(Boolean)
    : [];

const getAssetType = (asset) => asset.assetType || "person";

const getAssetName = (asset) => {
  const type = getAssetType(asset);
  if (type === "person") {
    return [asset.firstname, asset.lastname].filter(Boolean).join(" ") || "Unnamed person";
  }
  return asset.lastname || asset.firstname || "Unnamed asset";
};

const getAssetIcon = (type) => {
  const Icon = ASSET_TYPES.find((item) => item.value === type)?.icon || PersonOutlineIcon;
  return <Icon />;
};

const AssetAvatar = ({ asset }) => {
  const [failed, setFailed] = useState(false);
  const photo = asset.photo;

  if (!photo || photo === "none" || failed) {
    return <AccountCircleIcon className="monitoringAvatarFallback" />;
  }

  return (
    <img
      src={photo}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const MonitoringModal = ({ isOpen, mode, context, userToken, onClose, onChanged, navigate }) => {
  const [groupName, setGroupName] = useState("");
  const [originalGroupName, setOriginalGroupName] = useState("");
  const [originalSearchTerms, setOriginalSearchTerms] = useState("");
  const [assetType, setAssetType] = useState("person");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [name, setName] = useState("");
  const [nickName, setNickName] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [searchTerms, setSearchTerms] = useState([]);
  const [termInput, setTermInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setErrorMessage("");
    setTermInput("");

    if (mode === "create-group") {
      setGroupName("");
      setOriginalGroupName("");
      setOriginalSearchTerms("");
      setSearchTerms([]);
      setAssetType("person");
      setFirstName("");
      setLastName("");
      setName("");
      setNickName("");
      setPhotoUrl("");
      return;
    }

    if (mode === "edit-group") {
      setGroupName(context.groupName || "");
      setOriginalGroupName(context.groupName || "");
      setOriginalSearchTerms(context.searchTerm || "");
      setSearchTerms(splitTerms(context.searchTerm));
      return;
    }

    if (mode === "add-asset") {
      setGroupName(context.groupName || "");
      setAssetType("person");
      setFirstName("");
      setLastName("");
      setName("");
      setNickName("");
      setPhotoUrl("");
      setSearchTerms([]);
      return;
    }

    if (mode === "edit-asset") {
      const nextType = context.assetType || "person";
      setGroupName(context.groupName || "");
      setAssetType(nextType);
      setFirstName(context.firstName || "");
      setLastName(context.lastName || "");
      setName(nextType === "person" ? "" : context.lastName || context.firstName || "");
      setNickName(context.nickName || "");
      setPhotoUrl(context.photoUrl === "none" ? "" : context.photoUrl || "");
      setSearchTerms(splitTerms(context.searchTerm));
    }
  }, [isOpen, mode, context]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSubmitting) onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.classList.add("monitoringModalOpen");

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("monitoringModalOpen");
    };
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const isGroupMode = mode === "create-group" || mode === "edit-group";
  const isEditMode = mode === "edit-group" || mode === "edit-asset";
  const isPerson = assetType === "person";

  const titles = {
    "create-group": "Create monitoring group",
    "edit-group": "Edit monitoring group",
    "add-asset": `Add asset to ${context.groupName || "group"}`,
    "edit-asset": `Edit ${context.firstName || context.lastName || "asset"}`,
  };

  const addSearchTerm = () => {
    const trimmed = termInput.trim();
    if (trimmed && !searchTerms.includes(trimmed)) {
      setSearchTerms((current) => [...current, trimmed]);
    }
    setTermInput("");
  };

  const handleTermKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addSearchTerm();
    }
  };

  const handleUnauthorized = (response) => {
    if (response.status === 403) {
      navigate("/login");
      return true;
    }
    return false;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage("");
    const formattedTerms = searchTerms.join(", ");

    try {
      let response;

      if (mode === "create-group") {
        const formData = {
          edit_groupName: groupName,
          edit_firstName: "MainGroup",
          edit_lastName: "MainGroup",
          edit_nickName: "MainGroup",
          edit_photoUrl: "none",
          edit_searchTerm: formattedTerms,
          userToken,
        };

        response = await fetch("https://api.weathervaneintel.com/api/api/addgroup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify([formData]),
        });
      }

      if (mode === "edit-group") {
        const formData = {
          originalGroupName,
          edit_groupName: groupName,
          originalSearchTerms,
          edit_searchTerm: formattedTerms,
          userToken,
        };

        response = await fetch("https://api.weathervaneintel.com/api/api/editgroup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
      }

      if (mode === "add-asset") {
        const formData = {
          groupName,
          firstName: isPerson ? firstName : name,
          lastName: isPerson ? lastName : name,
          nickName: isPerson ? nickName : "",
          photoUrl,
          searchTerm: formattedTerms,
          assetType,
          userToken,
        };

        response = await fetch("https://api.weathervaneintel.com/api/api/addprotectedperson", {
          method: "POST",
          headers: { "Content-Type": "application/json", userToken },
          body: JSON.stringify(formData),
        });
      }

      if (mode === "edit-asset") {
        const formData = {
          id_num: context.idNum,
          edit_groupName: groupName,
          edit_firstName: isPerson ? firstName : name,
          edit_lastName: isPerson ? lastName : name,
          edit_nickName: isPerson ? nickName : "",
          edit_photoUrl: photoUrl,
          edit_searchTerm: formattedTerms,
          editAssetType: assetType,
          userToken,
        };

        response = await fetch("https://api.weathervaneintel.com/api/api/editprotectedperson", {
          method: "POST",
          headers: { "Content-Type": "application/json", userToken },
          body: JSON.stringify(formData),
        });
      }

      if (!response) throw new Error("Unknown modal mode");
      if (handleUnauthorized(response)) return;
      if (!response.ok) throw new Error(`API request failed: ${response.status}`);

      onChanged();
      onClose();
    } catch (error) {
      console.error(error);
      setErrorMessage("The change could not be saved. Please review the form and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || isSubmitting) return;

    const warning =
      mode === "edit-group"
        ? "This will delete the group and all persons listed. Are you sure you want to continue?"
        : "Deleting a protectee is final. Continue?";

    if (!window.confirm(warning)) return;

    setIsSubmitting(true);
    setErrorMessage("");
    const formattedTerms = searchTerms.join(", ");

    try {
      let response;

      if (mode === "edit-group") {
        const formData = {
          originalGroupName,
          edit_groupName: groupName,
          edit_search_term: formattedTerms,
          userToken,
        };

        response = await fetch("https://api.weathervaneintel.com/api/api/deletegroup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
      }

      if (mode === "edit-asset") {
        const formData = {
          id_num: context.idNum,
          edit_groupName: groupName,
          edit_firstName: isPerson ? firstName : name,
          edit_lastName: isPerson ? lastName : name,
          edit_nickName: isPerson ? nickName : "",
          edit_photoUrl: photoUrl,
          edit_searchTerm: formattedTerms,
          assetType,
          userToken,
        };

        response = await fetch("https://api.weathervaneintel.com/api/api/deleteprotectedperson", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
      }

      if (!response) throw new Error("Unknown delete mode");
      if (handleUnauthorized(response)) return;
      if (!response.ok) throw new Error(`API request failed: ${response.status}`);

      onChanged();
      onClose();
    } catch (error) {
      console.error(error);
      setErrorMessage("The item could not be deleted. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="monitoringModalBackdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onClose();
      }}
    >
      <section
        className="monitoringModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="monitoring-modal-title"
      >
        <header className="monitoringModalHeader">
          <div>
            <span className="monitoringEyebrow">Monitoring configuration</span>
            <h2 id="monitoring-modal-title">{titles[mode]}</h2>
          </div>
          <button
            type="button"
            className="monitoringIconButton"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <CloseIcon />
          </button>
        </header>

        <form className="monitoringForm" onSubmit={handleSubmit}>
          {!isGroupMode && (
            <fieldset className="monitoringTypeSelector">
              <legend>Asset type</legend>
              <div className="monitoringTypeGrid">
                {ASSET_TYPES.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    className={assetType === value ? "selected" : ""}
                    onClick={() => {
                      setAssetType(value);
                      if (value !== "person" && !name) {
                        setName(lastName || firstName || "");
                      }
                    }}
                    aria-pressed={assetType === value}
                  >
                    <Icon />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          {isGroupMode && (
            <div className="monitoringField">
              <label htmlFor="monitoring-group-name">Group name</label>
              <input
                id="monitoring-group-name"
                type="text"
                value={groupName}
                onChange={(event) => setGroupName(event.target.value)}
                placeholder="Example: Executive Leadership"
                required
              />
              <small>Use a clear name that describes the assets monitored together.</small>
            </div>
          )}

          {!isGroupMode && isPerson && (
            <div className="monitoringTwoColumnFields">
              <div className="monitoringField">
                <label htmlFor="monitoring-first-name">First name</label>
                <input
                  id="monitoring-first-name"
                  type="text"
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  placeholder="First name"
                  required
                />
              </div>
              <div className="monitoringField">
                <label htmlFor="monitoring-last-name">Last name</label>
                <input
                  id="monitoring-last-name"
                  type="text"
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  placeholder="Last name"
                  required
                />
              </div>
            </div>
          )}

          {!isGroupMode && !isPerson && (
            <div className="monitoringField">
              <label htmlFor="monitoring-asset-name">
                {ASSET_TYPES.find((item) => item.value === assetType)?.label} name
              </label>
              <input
                id="monitoring-asset-name"
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={`Enter ${assetType} name`}
                required
              />
            </div>
          )}

          {!isGroupMode && isPerson && (
            <div className="monitoringField">
              <label htmlFor="monitoring-nickname">Nickname</label>
              <input
                id="monitoring-nickname"
                type="text"
                value={nickName}
                onChange={(event) => setNickName(event.target.value)}
                placeholder="Optional alias or nickname"
              />
            </div>
          )}

          {!isGroupMode && (
            <div className="monitoringField">
              <label htmlFor="monitoring-photo-url">Photo URL</label>
              <input
                id="monitoring-photo-url"
                type="url"
                value={photoUrl}
                onChange={(event) => setPhotoUrl(event.target.value)}
                placeholder="https://..."
              />
              <small>Optional. Leave blank to use the default asset icon.</small>
            </div>
          )}

          <div className="monitoringField">
            <label htmlFor="monitoring-search-terms">Search terms</label>
            <div className="monitoringChipInput">
              {searchTerms.length > 0 && (
                <div className="monitoringChipList">
                  {searchTerms.map((term) => (
                    <span className="monitoringChip" key={term}>
                      {term}
                      <button
                        type="button"
                        onClick={() =>
                          setSearchTerms((current) => current.filter((item) => item !== term))
                        }
                        aria-label={`Remove ${term}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="monitoringTermEntry">
                <input
                  id="monitoring-search-terms"
                  type="text"
                  value={termInput}
                  onChange={(event) => setTermInput(event.target.value)}
                  onKeyDown={handleTermKeyDown}
                  onBlur={addSearchTerm}
                  placeholder="Type a term and press Enter"
                />
                <button type="button" onClick={addSearchTerm} disabled={!termInput.trim()}>
                  Add term
                </button>
              </div>
            </div>
            <small>Add names, phrases, aliases, or locations that should be monitored.</small>
          </div>

          {errorMessage && <div className="monitoringFormError">{errorMessage}</div>}

          <footer className="monitoringModalFooter">
            {isEditMode ? (
              <button
                type="button"
                className="monitoringDangerButton"
                onClick={handleDelete}
                disabled={isSubmitting}
              >
                <DeleteOutlineIcon />
                Delete
              </button>
            ) : (
              <span />
            )}

            <div className="monitoringFooterActions">
              <button
                type="button"
                className="monitoringSecondaryButton"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button type="submit" className="monitoringPrimaryButton" disabled={isSubmitting}>
                {isSubmitting
                  ? "Saving..."
                  : mode === "create-group"
                    ? "Create group"
                    : mode === "add-asset"
                      ? "Add asset"
                      : "Save changes"}
              </button>
            </div>
          </footer>
        </form>
      </section>
    </div>,
    document.getElementById("portal")
  );
};

export const ProtectedPersons = () => {
  const [groupJson, setGroupJson] = useState([]);
  const [userToken, setUserToken] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [searchValue, setSearchValue] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [openMenu, setOpenMenu] = useState(null);
  const [modalState, setModalState] = useState({ isOpen: false, mode: null, context: {} });
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const getUserToken = async () => {
      try {
        const session = await fetchAuthSession();
        const access_token = session.tokens.accessToken.toString();
        setUserToken(access_token);
      } catch {
        navigate("/login");
      }
    };

    getUserToken();
  }, [navigate]);

  useEffect(() => {
    if (userToken === null) return;

    const fetchGroups = async () => {
      setIsLoading(true);
      setLoadError("");

      try {
        const response = await fetch("https://api.weathervaneintel.com/api/api/getgroups", {
          method: "POST",
          headers: { "Content-Type": "application/json", access_token: userToken },
          body: JSON.stringify([{ userToken }]),
        });

        if (response.status === 403) {
          navigate("/login");
          return;
        }

        if (!response.ok) {
          console.warn("API Error:", response.status, response.statusText);
          setLoadError("Monitoring groups could not be loaded.");
          return;
        }

        const data = await response.json();
        setGroupJson(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Network error:", error);
        setLoadError("Monitoring groups could not be loaded. Check your connection and try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchGroups();
  }, [userToken, refreshKey, navigate]);

  useEffect(() => {
    const closeMenus = () => setOpenMenu(null);
    document.addEventListener("click", closeMenus);
    return () => document.removeEventListener("click", closeMenus);
  }, []);

  const assets = useMemo(
    () =>
      groupJson.flatMap((group) =>
        (group.members || []).filter((member) => member.firstname !== "MainGroup")
      ),
    [groupJson]
  );

  const filteredGroups = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return groupJson
      .filter((group) => groupFilter === "all" || group.groupname === groupFilter)
      .map((group) => {
        const filteredMembers = (group.members || []).filter((member) => {
          if (member.firstname === "MainGroup") return false;

          const matchesType = typeFilter === "all" || getAssetType(member) === typeFilter;
          const searchableValues = [
            member.firstname,
            member.lastname,
            member.nickname,
            member.search_terms,
            member.assetType,
            group.groupname,
          ];
          const matchesSearch =
            !normalizedSearch ||
            searchableValues
              .filter(Boolean)
              .some((value) => value.toLowerCase().includes(normalizedSearch));

          return matchesType && matchesSearch;
        });

        const groupNameMatches = group.groupname?.toLowerCase().includes(normalizedSearch);
        const shouldShow =
          !normalizedSearch || filteredMembers.length > 0 || (groupNameMatches && typeFilter === "all");

        return shouldShow ? { ...group, filteredMembers } : null;
      })
      .filter(Boolean);
  }, [groupJson, groupFilter, typeFilter, searchValue]);

  const openModal = (mode, context = {}) => {
    setOpenMenu(null);
    setModalState({ isOpen: true, mode, context });
  };

  const closeModal = () => setModalState({ isOpen: false, mode: null, context: {} });

  const toggleGroup = (groupName) => {
    setExpandedGroups((current) => ({
      ...current,
      [groupName]: !current[groupName],
    }));
  };

  if (userToken === null) {
    return (
      <div className="monitoringLoadingScreen">
        <div className="monitoringSpinner" />
        <p>Loading monitoring workspace...</p>
      </div>
    );
  }

  return (
    <div className="monitoringPage">
      <div className="monitoringPageGlow monitoringPageGlowOne" />
      <div className="monitoringPageGlow monitoringPageGlowTwo" />

      <main className="monitoringMain">
        <header className="monitoringPageHeader">
          <div className="monitoringHeaderCopy">
            <span className="monitoringEyebrow">Asset configuration</span>
            <h1>Monitoring</h1>
            <p>
              Organize the people, locations, events, and topics your intelligence workspace monitors.
            </p>
          </div>
          <button
            type="button"
            className="monitoringPrimaryButton monitoringNewGroupButton"
            onClick={() => openModal("create-group")}
          >
            <AddIcon />
            New group
          </button>
        </header>

        <section className="monitoringSummaryGrid" aria-label="Monitoring summary">
          <article className="monitoringGlassPanel monitoringSummaryCard">
            <span>Monitoring groups</span>
            <strong>{groupJson.length}</strong>
            <small>Organized collections of related assets</small>
          </article>
          <article className="monitoringGlassPanel monitoringSummaryCard">
            <span>Total assets</span>
            <strong>{assets.length}</strong>
            <small>People, locations, events, and topics</small>
          </article>
          <article className="monitoringGlassPanel monitoringSummaryCard">
            <span>Search terms</span>
            <strong>
              {assets.reduce((total, asset) => total + splitTerms(asset.search_terms).length, 0)}
            </strong>
            <small>Configured terms across all assets</small>
          </article>
        </section>

        <section className="monitoringGlassPanel monitoringToolbar" aria-label="Monitoring filters">
          <div className="monitoringGlobalSearch">
            <SearchIcon />
            <input
              type="search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search assets, groups, or monitoring terms"
              aria-label="Search monitoring configuration"
            />
          </div>

          <div className="monitoringFilterGroup">
            <select
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              aria-label="Filter by asset type"
            >
              <option value="all">All asset types</option>
              <option value="person">People</option>
              <option value="place">Locations</option>
              <option value="event">Events</option>
              <option value="topic">Topics</option>
            </select>
            <select
              value={groupFilter}
              onChange={(event) => setGroupFilter(event.target.value)}
              aria-label="Filter by group"
            >
              <option value="all">All groups</option>
              {groupJson.map((group) => (
                <option value={group.groupname} key={group.groupname}>
                  {group.groupname}
                </option>
              ))}
            </select>
          </div>
        </section>

        {loadError && (
          <div className="monitoringLoadError">
            <span>{loadError}</span>
            <button type="button" onClick={() => setRefreshKey((value) => value + 1)}>
              Try again
            </button>
          </div>
        )}

        {isLoading ? (
          <section className="monitoringCardGrid" aria-label="Loading groups">
            {[1, 2, 3].map((item) => (
              <div className="monitoringGroupSkeleton" key={item} />
            ))}
          </section>
        ) : filteredGroups.length === 0 ? (
          <section className="monitoringGlassPanel monitoringEmptyState">
            <SearchIcon />
            <h2>No matching monitoring assets</h2>
            <p>Change the search or filters, or create a new monitoring group.</p>
            <button
              type="button"
              className="monitoringPrimaryButton"
              onClick={() => openModal("create-group")}
            >
              <AddIcon />
              Create group
            </button>
          </section>
        ) : (
          <section className="monitoringCardGrid" aria-label="Monitoring groups">
            {filteredGroups.map((group) => {
              const isExpanded = Boolean(expandedGroups[group.groupname]);
              const assetCount = (group.members || []).filter(
                (member) => member.firstname !== "MainGroup"
              ).length;

              return (
                <article
                  className={`monitoringGroupCard monitoringGlassPanel ${isExpanded ? "expanded" : ""}`}
                  key={group.groupname}
                >
                  <header className="monitoringGroupHeader">
                    <button
                      type="button"
                      className="monitoringGroupIdentity"
                      onClick={() => toggleGroup(group.groupname)}
                      aria-expanded={isExpanded}
                    >
                      <span className="monitoringGroupIcon">
                        <FolderOutlinedIcon />
                      </span>
                      <span className="monitoringGroupTitle">
                        <strong>{group.groupname}</strong>
                        <small>{assetCount} {assetCount === 1 ? "asset" : "assets"}</small>
                      </span>
                    </button>

                    <div className="monitoringGroupActions">
<button
  type="button"
  className="monitoringAddAssetButton"
  onClick={() =>
    openModal("add-asset", {
      groupName: group.groupname
    })
  }
  aria-label={`Add asset to ${group.groupname}`}
  title={`Add asset to ${group.groupname}`}
>
  <AddIcon aria-hidden="true" />
  <span className="monitoringAddAssetLabel">
    Add asset
  </span>
</button>

                      <div className="monitoringMenuWrapper">
                        <button
                          type="button"
                          className="monitoringIconButton"
                          aria-label={`Actions for ${group.groupname}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setOpenMenu((current) =>
                              current === group.groupname ? null : group.groupname
                            );
                          }}
                        >
                          <MoreVertIcon />
                        </button>

                        {openMenu === group.groupname && (
                          <div
                            className="monitoringActionMenu"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                openModal("edit-group", {
                                  groupName: group.groupname,
                                  searchTerm: group.groupData,
                                })
                              }
                            >
                              <EditOutlinedIcon />
                              Edit group
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                openModal("add-asset", { groupName: group.groupname })
                              }
                            >
                              <AddIcon />
                              Add asset
                            </button>
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        className="monitoringIconButton monitoringExpandButton"
                        onClick={() => toggleGroup(group.groupname)}
                        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${group.groupname}`}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </button>
                    </div>
                  </header>

                  {isExpanded && (
                    <div className="monitoringAssetList">
                      {group.filteredMembers.length === 0 ? (
                        <div className="monitoringGroupEmpty">
                          <p>No assets match the current filters.</p>
                          <button
                            type="button"
                            onClick={() => openModal("add-asset", { groupName: group.groupname })}
                          >
                            Add an asset
                          </button>
                        </div>
                      ) : (
                        group.filteredMembers.map((asset) => {
                          const type = getAssetType(asset);
                          const terms = splitTerms(asset.search_terms);

                          return (
                            <button
                              type="button"
                              className="monitoringAssetCard"
                              key={asset.id_num}
                              onClick={() =>
                                openModal("edit-asset", {
                                  idNum: asset.id_num,
                                  groupName: asset.group_name,
                                  firstName: asset.firstname,
                                  lastName: asset.lastname,
                                  nickName: asset.nickname,
                                  photoUrl: asset.photo,
                                  searchTerm: asset.search_terms,
                                  assetType: type,
                                })
                              }
                            >
                              <span className="monitoringAssetAvatar">
                                <AssetAvatar asset={asset} />
                              </span>

                              <span className="monitoringAssetDetails">
                                <span className="monitoringAssetTitleRow">
                                  <strong>{getAssetName(asset)}</strong>
                                  <span className={`monitoringAssetBadge ${type}`}>
                                    {getAssetIcon(type)}
                                    {ASSET_TYPES.find((item) => item.value === type)?.label || "Person"}
                                  </span>
                                </span>

                                <span className="monitoringAssetSubtitle">
                                  {type === "person" && asset.nickname
                                    ? `Also known as ${asset.nickname}`
                                    : terms.length > 0
                                      ? `${terms.length} monitoring ${terms.length === 1 ? "term" : "terms"}`
                                      : "No monitoring terms configured"}
                                </span>

                                {terms.length > 0 && (
                                  <span className="monitoringTermPreview">
                                    {terms.slice(0, 2).map((term) => (
                                      <span key={term}>{term}</span>
                                    ))}
                                    {terms.length > 2 && <span>+{terms.length - 2}</span>}
                                  </span>
                                )}
                              </span>

                              <ChevronRightIcon className="monitoringAssetChevron" />
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </section>
        )}
      </main>

      <MonitoringModal
        isOpen={modalState.isOpen}
        mode={modalState.mode}
        context={modalState.context}
        userToken={userToken}
        onClose={closeModal}
        onChanged={() => setRefreshKey((value) => value + 1)}
        navigate={navigate}
      />
    </div>
  );
};
